import { createApi } from '@reduxjs/toolkit/query/react'
import { graphqlRequestBaseQuery } from '@rtk-query/graphql-request-base-query'
import {
  AUTH_REDUCER_KEY,
  Permission,
  RootStateWithAuth,
} from '@valerahealth/redux-auth'
import { ErrorResponse } from '@rtk-query/graphql-request-base-query/dist/GraphqlBaseQueryTypes'
import { api as endpoints } from './generated'
import { createService, initApplyPermissionheaders } from '../../utils'
import { patientRegistrationApi } from '../patientRegistration'

const applyPermissionHeaders = initApplyPermissionheaders({
  verifyEligibility: Permission.Patient_Update,
})

const getBaseApi = (
  origin: string,
  getAccessToken: (state: any) => string | null | undefined,
) =>
  createApi({
    reducerPath: 'verifyApi',
    tagTypes: ['billing'],
    keepUnusedDataFor: 0,
    baseQuery: graphqlRequestBaseQuery({
      url: `${origin}/graphql`,
      customErrors: (error) => {
        console.error(error.response)
        const e = error.response.errors?.[0] as any
        const response: ErrorResponse = {
          name: e?.errorType || '',
          message: e?.message || '',
          stack: error.stack || '',
        }
        return response
      },
      prepareHeaders: (headers, api) => {
        const state = api.getState() as RootStateWithAuth
        const { isAuthenticated, session } = state[AUTH_REDUCER_KEY]
        if (isAuthenticated) {
          const accessToken = getAccessToken(state)
          headers.set('Authorization', `Bearer ${accessToken}`)
          applyPermissionHeaders(headers, session!, api.endpoint)
        }
        return headers
      },
    }),
    endpoints,
  })

type BaseApiType = ReturnType<typeof getBaseApi>

const enhanceEndpoints = (api: BaseApiType) => api.enhanceEndpoints({
  endpoints: {
    verifyEligibility: {
        invalidatesTags: (res, err, req) => [
          { type: 'billing', id: req.input.treatmentId },
        ],
        /** code that updates data in place rather than requiring a refetch */
        async onQueryStarted(payload, { queryFulfilled }) {
          const { data } = await queryFulfilled.catch(() => ({
            data: { verifyEligibility: null },
          }))

          const verification = data.verifyEligibility
          if (verification) {
            patientRegistrationApi.util.invalidateTags([
              {type: 'eligibilityVerification', id: payload.input.billingInsuranceId || ''},
              {type: 'billing', id: payload.input.treatmentId || ''}
            ])
          }
        },
      },
  }
})

const verifyGQLService = createService(
  ({origin, getAccessToken}) => {
    const api = getBaseApi(origin, getAccessToken)
    return enhanceEndpoints(api)
  }
)

export const verifyApi = verifyGQLService({
  origin:  `https://${process.env.VERIFY_API_DOMAIN}`,
})

export type VerifyApiType = typeof verifyApi
